import type { FormEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChildSelector } from '@/components/builder/ChildSelector';
import { MediaOptionsGroup } from '@/components/builder/MediaOptionsGroup';
import { CategoryChips, type SbCategory } from '@/components/builder/sb/CategoryChips';
import { ModeSelector, type SbMode } from '@/components/builder/sb/ModeSelector';
import { ModeCard } from '@/components/builder/series/SeriesModeToggle';
import { useCatalogEnabled } from '@/hooks/useCatalogEnabled';
import { QuestionCards, type SbQuestion } from '@/components/builder/sb/QuestionCards';
import { QuestionInput } from '@/components/builder/sb/QuestionInput';
import { TabSwitcher } from '@/components/builder/sb/TabSwitcher';
import { SubmitButton } from '@/components/builder/SubmitButton';
import { VoiceSelector, type Voice } from '@/components/builder/VoiceSelector';
import { BuilderGate } from '@/components/builder/BuilderGate';
import { useBuilderUrlPrefill } from './use-builder-url-prefill';
import { clearEntitlementCache } from '@/hooks/useEntitlement';
import { clearSubscriptionStateCache } from '@/hooks/useSubscriptionState';
import { useSbQuestions } from '@/hooks/useSbQuestions';
import { useStoryWorlds } from '@/hooks/useStoryWorlds';
import { apiFetch, ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

type SbTab = 'custom' | 'random' | 'category';

const QUESTION_BATCH_SIZE = 6;

// POST /api/stories/jobs body — SB-specific shape.
// Source: src/story-jobs/dto/create-story-job.dto.ts.
// Differs from AA/KP: uses mode + userQuestion instead of
// inputType/customTheme/storyDuration.
interface CreateStoryJobRequest {
  worldId: string;
  childId: string;
  mode: 'story' | 'explanation';
  userQuestion: string;
  narratorGender: 'male' | 'female';
  wantsAudio: boolean;
  wantsPdf: boolean;
}

interface CreateStoryJobResponse {
  jobId: string;
  status: string;
  totalSteps: number;
}

export function SBBuilderRoute() {
  const S = STRINGS.builder.shared;
  const SSB = STRINGS.builder.sb;
  const navigate = useNavigate();
  const { getWorldIdBySlug } = useStoryWorlds();

  // Batch 10 Sub-phase E — URL pre-fill via shared hook. Decision
  // #14B (Sebastian Gate-1 2026-04-27): SB accepts `?theme=` as the
  // primary source (briefing-compliance, IdeaTile contract) AND
  // `?question=` as a back-compat alias for legacy SB-Question links.
  // Theme wins precedence when both are present.
  const { initialValue: initialQuestion } = useBuilderUrlPrefill({
    aliasParam: 'question',
  });

  const { enabled: catalogEnabled } = useCatalogEnabled();
  const [activeTab, setActiveTab] = useState<SbTab>('custom');
  const [mode, setMode] = useState<SbMode>('story');
  const [customQuestion, setCustomQuestion] = useState(initialQuestion ?? '');
  const [selectedCategory, setSelectedCategory] = useState<SbCategory | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<SbQuestion | null>(null);
  const [excludeIds, setExcludeIds] = useState<string[]>([]);

  const [childId, setChildId] = useState<string | null>(null);
  const [pdf, setPdf] = useState(false);
  const [audio, setAudio] = useState(false);
  const [voice, setVoice] = useState<Voice>('male');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitDisabled = (() => {
    if (!childId) return true;
    if (activeTab === 'custom') return !customQuestion.trim();
    return !selectedQuestion;
  })();

  const handleTabChange = (id: string) => {
    setActiveTab(id as SbTab);
    setSelectedQuestion(null);
    setExcludeIds([]);
  };

  const handleCategoryChange = (cat: SbCategory | null) => {
    setSelectedCategory(cat);
    setSelectedQuestion(null);
    setExcludeIds([]);
  };

  const handleRequestMore = (currentIds: string[]) => {
    setExcludeIds((prev) => [...prev, ...currentIds]);
    setSelectedQuestion(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled || isSubmitting) return;

    const worldId = getWorldIdBySlug('sb');
    if (!worldId || !childId) {
      console.error('[SBBuilder] missing worldId or childId', { worldId, childId });
      return;
    }

    // Derive userQuestion from the current tab's source of truth. Never from
    // selectedCategory — that's UI scope only.
    let userQuestion: string;
    if (activeTab === 'custom') {
      userQuestion = customQuestion.trim();
    } else {
      if (!selectedQuestion) return;
      userQuestion = selectedQuestion.question;
    }

    const payload: CreateStoryJobRequest = {
      worldId,
      childId,
      mode,
      userQuestion,
      narratorGender: voice,
      wantsAudio: audio,
      wantsPdf: pdf,
    };

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const response = await apiFetch<CreateStoryJobResponse>('/api/stories/jobs', {
        method: 'POST',
        body: payload,
      });
      // B1 (Bug 2): clear-only (no eager refetch — deduction commits at pipeline
      // step 6, after this POST returns). B2/B3 refresh after it settles.
      clearEntitlementCache();
      clearSubscriptionStateCache();
      navigate(`/output/${response.jobId}`, {
        state: { world: 'sb', wantsAudio: audio, wantsPdf: pdf },
      });
      // Intentionally don't reset isSubmitting — we're navigating away.
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.envelope?.user_message ?? STRINGS.auth.errors.genericBackend);
      } else if (err instanceof NetworkError) {
        setSubmitError(STRINGS.auth.errors.networkError);
      } else {
        setSubmitError(STRINGS.auth.errors.genericBackend);
      }
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'custom' as const, label: SSB.tabs.custom },
    { id: 'random' as const, label: SSB.tabs.random },
    { id: 'category' as const, label: SSB.tabs.category },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="mx-auto max-w-[560px] px-6 pt-6 pb-12">
        <div className="mb-6 flex justify-center">
          <img
            src="/worlds/sb/fox.png"
            alt=""
            aria-hidden="true"
            className="block h-[120px] w-auto md:h-[180px] desktop:h-[240px]"
          />
        </div>
        <h1 className="text-h3 mb-6 font-semibold text-dream-navy">{SSB.pageTitle}</h1>

        {/* Block D — SB level-1 Erzählform (catalog-flag-gated). This page IS the
            Einzelgeschichte path; "Wissenswelten (Serienformat)" routes to the
            curated catalog browse. Flag OFF → not rendered → SB builder = today. */}
        {catalogEnabled && (
          <div className="mb-6 space-y-1.5">
            <span className="text-sm text-dream-navy">{STRINGS.catalog.builder.modeLabel}</span>
            <div className="grid grid-cols-2 gap-3">
              <ModeCard
                active
                onClick={() => {}}
                label={STRINGS.catalog.builder.single}
                description={STRINGS.catalog.builder.singleSub}
              />
              <ModeCard
                active={false}
                onClick={() => navigate('/catalog')}
                label={STRINGS.catalog.builder.catalog}
                description={STRINGS.catalog.builder.catalogSub}
              />
            </div>
          </div>
        )}

        <BuilderGate>
          <form onSubmit={handleSubmit}>
            <fieldset disabled={isSubmitting} className="flex flex-col gap-5">
              <TabSwitcher tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

              <div className="space-y-2">
                <Label className="text-sm text-dream-navy">{SSB.modeSelectorLabel}</Label>
                <ModeSelector value={mode} onChange={setMode} />
              </div>

              {activeTab === 'custom' && (
                <div className="space-y-1.5">
                  <Label htmlFor="sb-question" className="text-sm text-dream-navy">
                    {SSB.questionInputLabel}
                  </Label>
                  <QuestionInput
                    id="sb-question"
                    value={customQuestion}
                    onChange={setCustomQuestion}
                    placeholder={SSB.questionInputPlaceholder}
                    maxLength={300}
                  />
                  <p className="mt-1 text-xs text-dream-navy/60">{SSB.questionInputHint}</p>
                </div>
              )}

              {activeTab === 'random' && (
                <QuestionListSection
                  mode="random"
                  excludeIds={excludeIds}
                  selectedQuestionId={selectedQuestion?.id ?? null}
                  onSelect={setSelectedQuestion}
                  onRequestMore={handleRequestMore}
                />
              )}

              {activeTab === 'category' && (
                <div className="flex flex-col gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm text-dream-navy">{SSB.categoriesLabel}</Label>
                    <CategoryChips value={selectedCategory} onChange={handleCategoryChange} />
                  </div>
                  {selectedCategory && (
                    <QuestionListSection
                      key={selectedCategory}
                      mode="category"
                      category={selectedCategory}
                      excludeIds={excludeIds}
                      selectedQuestionId={selectedQuestion?.id ?? null}
                      onSelect={setSelectedQuestion}
                      onRequestMore={handleRequestMore}
                      label={SSB.questionsLabel}
                    />
                  )}
                </div>
              )}

              <div className="border-t border-dream-navy/15" />

              <ChildSelector
                id="sb-child"
                label={S.childSelectorLabel}
                value={childId}
                onChange={setChildId}
              />

              <div className="space-y-2">
                <Label className="text-sm text-dream-navy">{S.optionsLabel}</Label>
                <MediaOptionsGroup
                  pdfActive={pdf}
                  audioActive={audio}
                  onPdfChange={setPdf}
                  onAudioChange={setAudio}
                  selectedBorderClassName="border-dream-navy"
                />
              </div>

              {audio && (
                <VoiceSelector
                  value={voice}
                  onChange={setVoice}
                  selectedBorderClassName="border-dream-navy"
                />
              )}

              {submitError && (
                <div
                  role="alert"
                  className="rounded-md border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-3 text-sm text-[color:var(--color-error)]"
                >
                  {submitError}
                </div>
              )}

              <SubmitButton disabled={submitDisabled} loading={isSubmitting} type="submit" />
            </fieldset>
          </form>
        </BuilderGate>
      </div>
    </div>
  );
}

interface QuestionListSectionProps {
  mode: 'category' | 'random';
  category?: SbCategory;
  excludeIds: string[];
  selectedQuestionId: string | null;
  onSelect: (question: SbQuestion) => void;
  onRequestMore: (currentIds: string[]) => void;
  label?: string;
}

function QuestionListSection({
  mode,
  category,
  excludeIds,
  selectedQuestionId,
  onSelect,
  onRequestMore,
  label,
}: QuestionListSectionProps) {
  const { questions, isLoading } = useSbQuestions({
    mode,
    limit: QUESTION_BATCH_SIZE,
    excludeIds,
    ...(category !== undefined && { category }),
  });

  const loadingMore = isLoading && excludeIds.length > 0;
  const poolExhausted =
    !isLoading && excludeIds.length > 0 && questions.length < QUESTION_BATCH_SIZE;

  const handleMore = () => {
    if (isLoading || poolExhausted) return;
    onRequestMore(questions.map((q) => q.id));
  };

  return (
    <div className="space-y-3">
      {label && <Label className="text-sm text-dream-navy">{label}</Label>}
      <QuestionCards
        questions={questions}
        selectedQuestionId={selectedQuestionId}
        onSelect={onSelect}
        isLoading={isLoading}
      />
      <Button
        type="button"
        onClick={handleMore}
        disabled={isLoading || poolExhausted}
        className="h-11 w-full rounded-lg border-[1.5px] border-dream-gold bg-dream-gold/10 text-base font-semibold text-dream-navy hover:bg-dream-gold/20 disabled:opacity-60"
      >
        {loadingMore ? (
          <>
            <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
            Lade…
          </>
        ) : (
          STRINGS.builder.sb.moreQuestionsButton
        )}
      </Button>
    </div>
  );
}
